import React, { useState } from "react";
import { Table } from "react-bootstrap";

const bulkIconStyle = (size) => ({
  width: size,
  height: size,
  minWidth: size,
  minHeight: size,
  display: "inline-block",
  verticalAlign: "middle",
  flexShrink: 0,
});

const BulkDownloadIcon = ({ size = 13 }) => (
  <svg style={bulkIconStyle(size)} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const BulkInfoIcon = ({ size = 13 }) => (
  <svg style={{ ...bulkIconStyle(size), marginRight: 4 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const BulkExcelIcon = ({ size = 53 }) => (
  <svg
    viewBox="0 0 48 48"
    fill="none"
    style={{ display: "block", margin: "0 auto 4px", width: size, height: size, minWidth: size, minHeight: size, flexShrink: 0 }}
  >
    <path d="M14 10h14l10 10v22a2 2 0 0 1-2 2H14a2 2 0 0 1-2-2V12a2 2 0 0 1 2-2z" fill="#dcfce7" stroke="#22c55e" strokeWidth="1.5" />
    <path d="M28 10v10h10" stroke="#22c55e" strokeWidth="1.5" strokeLinejoin="round" fill="#bbf7d0" />
    <line x1="13" y1="29" x2="19" y2="38" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="19" y1="29" x2="13" y2="38" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="22" y1="29" x2="22" y2="38" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="22" y1="38" x2="27" y2="38" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M36 30.5c0-1-1-1.5-2.5-1.5s-2.5.6-2.5 1.5c0 2 5 1.5 5 3.5 0 1.1-1.1 2-2.5 2s-2.5-.7-2.5-2" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" fill="none" />
  </svg>
);

const BulkDropZone = ({ file, dragOver, disabled, onDragOver, onDragLeave, onDrop, onChange, onClear }) => (
  <div
    onDragOver={onDragOver}
    onDragLeave={onDragLeave}
    onDrop={onDrop}
    style={{
      position: "relative",
      borderRadius: 10,
      border: `2px dashed ${dragOver ? "#3f88a5" : file ? "#22c55e" : "#cbd5e1"}`,
      background: dragOver ? "#f0f9ff" : file ? "#f0fdf4" : "#fafbfc",
      padding: "28px 20px",
      textAlign: "center",
      transition: "all .2s",
      opacity: disabled ? 0.7 : 1,
    }}
  >
    {file ? (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
        <BulkExcelIcon />
        <span style={{ fontWeight: 600, color: "#166534", fontSize: 14 }}>{file.name}</span>
        <span style={{ fontSize: 12, color: "#6b7280" }}>{(file.size / 1024).toFixed(1)} KB</span>
        <button
          type="button"
          onClick={onClear}
          disabled={disabled}
          style={{
            marginTop: 4,
            background: "transparent",
            border: "1px solid #fca5a5",
            color: "#ef4444",
            borderRadius: 6,
            padding: "3px 10px",
            fontSize: 12,
            fontWeight: 600,
            cursor: disabled ? "not-allowed" : "pointer",
          }}
        >
          Remove
        </button>
      </div>
    ) : (
      <>
        <BulkExcelIcon />
        <p style={{ color: "#64748b", fontSize: 14, margin: "8px 0 2px" }}>
          <strong>Drag & drop</strong> or <span style={{ color: "#3f88a5" }}>browse</span>
        </p>
        <p style={{ color: "#94a3b8", fontSize: 12, margin: 0 }}>Two columns: company_name, email · .xlsx</p>
      </>
    )}
    <input
      type="file"
      accept=".xlsx"
      onChange={onChange}
      disabled={disabled}
      style={{
        position: "absolute",
        inset: 0,
        opacity: 0,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    />
  </div>
);

const BulkUploadPanel = ({
  file,
  uploading,
  result,
  onFileChange,
  onFileClear,
  onDownloadTemplate,
}) => {
  const [dragOver, setDragOver] = useState(false);
  const sectionTitle = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    color: "#94a3b8",
    textTransform: "uppercase",
    marginBottom: 12,
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div
        style={{
          background: "#f0f9ff",
          border: "1px solid #bae6fd",
          borderRadius: 10,
          padding: "10px 14px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 10,
        }}
      >
        <span style={{ fontSize: 13, color: "#0369a1", display: "flex", alignItems: "center" }}>
          <BulkInfoIcon /> Download the template, add rows, then drop it below.
        </span>
        <button
          type="button"
          onClick={onDownloadTemplate}
          disabled={uploading}
          style={{
            background: uploading ? "transparent" : "#0284c7",
            border: "1px solid #0284c7",
            color: uploading ? "#0284c7" : "#fff",
            borderRadius: 6,
            padding: "4px 12px",
            fontSize: 12,
            fontWeight: 600,
            cursor: uploading ? "not-allowed" : "pointer",
            opacity: uploading ? 0.6 : 1,
            display: "flex",
            alignItems: "center",
            gap: 5,
          }}
        >
          <BulkDownloadIcon /> Template
        </button>
      </div>

      <div>
        <span style={sectionTitle}>File</span>
        <BulkDropZone
          file={file}
          dragOver={dragOver}
          disabled={uploading}
          onDragOver={(e) => {
            e.preventDefault();
            if (!uploading) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (uploading) return;
            const dropped = e.dataTransfer?.files?.[0];
            if (dropped) onFileChange(dropped);
          }}
          onChange={(e) => onFileChange(e.target.files?.[0] || null)}
          onClear={onFileClear}
        />
      </div>

      {result ? (
        <div
          style={{
            background: "var(--surface)",
            border: "1px solid var(--rule)",
            borderRadius: 10,
            padding: 14,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)", marginBottom: 8 }}>
            Result
          </div>
          <div style={{ display: "flex", gap: 16, fontSize: 12, marginBottom: 10 }}>
            <span>
              Total rows: <strong>{result.total ?? 0}</strong>
            </span>
            <span style={{ color: "#15803d" }}>
              Sent: <strong>{result.succeeded ?? 0}</strong>
            </span>
            <span style={{ color: "#b91c1c" }}>
              Skipped: <strong>{result.skipped?.length ?? 0}</strong>
            </span>
          </div>
          {result.skipped?.length ? (
            <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid var(--rule)", borderRadius: 6 }}>
              <Table size="sm" style={{ marginBottom: 0, fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ background: "#f4f6f9" }}>Row</th>
                    <th style={{ background: "#f4f6f9" }}>Company</th>
                    <th style={{ background: "#f4f6f9" }}>Email</th>
                    <th style={{ background: "#f4f6f9" }}>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {result.skipped.map((r, idx) => (
                    <tr key={idx}>
                      <td>{r.row}</td>
                      <td>{r.company_name || "—"}</td>
                      <td>{r.email || "—"}</td>
                      <td style={{ color: "#b91c1c" }}>{r.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default BulkUploadPanel;
